/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Conversation } from '../models/Conversation';
import type { Message } from '../models/Message';
import type { MessageRequest } from '../models/MessageRequest';
import type { PaginatedConversationList } from '../models/PaginatedConversationList';
import type { PaginatedMessageList } from '../models/PaginatedMessageList';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class ConversationsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns PaginatedConversationList
     * @throws ApiError
     */
    public conversationsList({
        company,
        employee,
        limit,
        offset,
    }: {
        company?: string,
        employee?: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedConversationList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/conversations/',
            query: {
                'company': company,
                'employee': employee,
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * @returns PaginatedMessageList
     * @throws ApiError
     */
    public conversationsMessagesList({
        parentConversation,
        limit,
        offset,
    }: {
        parentConversation: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedMessageList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/conversations/{parent_conversation}/messages/',
            path: {
                'parent_conversation': parentConversation,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * @returns Message
     * @throws ApiError
     */
    public conversationsMessagesCreate({
        parentConversation,
        requestBody,
    }: {
        parentConversation: string,
        requestBody?: MessageRequest,
    }): CancelablePromise<Message> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/conversations/{parent_conversation}/messages/',
            path: {
                'parent_conversation': parentConversation,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns Message
     * @throws ApiError
     */
    public conversationsMessagesRetrieve({
        id,
        parentConversation,
    }: {
        /**
         * A UUID string identifying this message.
         */
        id: string,
        parentConversation: string,
    }): CancelablePromise<Message> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/conversations/{parent_conversation}/messages/{id}/',
            path: {
                'id': id,
                'parent_conversation': parentConversation,
            },
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns Conversation
     * @throws ApiError
     */
    public conversationsRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this conversation.
         */
        id: string,
    }): CancelablePromise<Conversation> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/conversations/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns void
     * @throws ApiError
     */
    public conversationsAcknowledgeReadUpdate({
        id,
    }: {
        /**
         * A UUID string identifying this conversation.
         */
        id: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/conversations/{id}/acknowledge_read/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns void
     * @throws ApiError
     */
    public conversationsArchiveConversationUpdate({
        id,
    }: {
        /**
         * A UUID string identifying this conversation.
         */
        id: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/conversations/{id}/archive_conversation/',
            path: {
                'id': id,
            },
        });
    }

}
